/** Phase4-R Lite：日报 ↔ 项目日报双写关联（localStorage） */

import type { ProjectWorkItemFormValue } from '@/types/report-project-work-item-lite';

export const REPORT_LITE_BUNDLE_SOURCE = 'daily-report-fusion' as const;

export interface ReportLiteBundleItem {
  projectReportId: string;
  projectId: string;
  sortOrder: number;
}

export interface ReportLiteBundle {
  reportId: string;
  reportDate: string;
  source: typeof REPORT_LITE_BUNDLE_SOURCE;
  items: ReportLiteBundleItem[];
}

function bundleStorageKey(userId: string, reportId: string): string {
  return `report-lite-bundle:${userId}:${reportId}`;
}

export function readReportLiteBundle(
  userId: string,
  reportId: string,
): ReportLiteBundle | null {
  try {
    const raw = localStorage.getItem(bundleStorageKey(userId, reportId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as ReportLiteBundle;
    if (parsed?.reportId !== reportId || parsed?.source !== REPORT_LITE_BUNDLE_SOURCE) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeReportLiteBundle(userId: string, bundle: ReportLiteBundle): void {
  localStorage.setItem(bundleStorageKey(userId, bundle.reportId), JSON.stringify(bundle));
}

/** T3.1：同步完成后全量替换 Bundle（含删除项清理） */
export function replaceReportLiteBundle(
  userId: string,
  reportId: string,
  reportDate: string,
  items: ProjectWorkItemFormValue[],
): void {
  const bundleItems: ReportLiteBundleItem[] = sortedBundleItems(items);
  if (bundleItems.length === 0) {
    removeReportLiteBundle(userId, reportId);
    return;
  }
  writeReportLiteBundle(userId, {
    reportId,
    reportDate,
    source: REPORT_LITE_BUNDLE_SOURCE,
    items: bundleItems,
  });
}

function sortedBundleItems(items: ProjectWorkItemFormValue[]): ReportLiteBundleItem[] {
  return [...items]
    .filter((item) => item.projectReportId)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) => ({
      projectReportId: item.projectReportId!,
      projectId: item.projectId,
      sortOrder: item.sortOrder,
    }));
}

export function mergeReportLiteBundleItems(
  userId: string,
  reportId: string,
  reportDate: string,
  newItems: ReportLiteBundleItem[],
): ReportLiteBundle {
  const existing = readReportLiteBundle(userId, reportId);
  const byProjectId = new Map<string, ReportLiteBundleItem>();

  for (const item of existing?.items ?? []) {
    byProjectId.set(item.projectId, item);
  }
  for (const item of newItems) {
    byProjectId.set(item.projectId, item);
  }

  const items = [...byProjectId.values()].sort((a, b) => a.sortOrder - b.sortOrder);
  const bundle: ReportLiteBundle = {
    reportId,
    reportDate,
    source: REPORT_LITE_BUNDLE_SOURCE,
    items,
  };
  writeReportLiteBundle(userId, bundle);
  return bundle;
}

export function removeReportLiteBundle(userId: string, reportId: string): void {
  localStorage.removeItem(bundleStorageKey(userId, reportId));
}
